/* eslint-disable react-refresh/only-export-components */
/**
 * Small displays shared by the accounts list and the account page: status, health, account
 * kind, next step and last touch. Values and tones come from crmConfig.ts.
 */
import { useLocaleState, useTranslate } from "ra-core";

import { Badge } from "@/components/ui/badge";
import { StatusDot } from "@/components/ui/status-dot";
import { cn } from "@/lib/utils";

import {
  ACCOUNT_KINDS,
  ACCOUNT_STATUSES,
  ACTIVATION_TARGET_DAYS,
  HEALTH_SEGMENTS,
} from "../providers/zinspector/crmConfig";
import type { ZcrmAccount } from "../providers/zinspector/accounts";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days from `date` to today (negative in the future); null without a valid date. */
export const daysSince = (
  date: string | null | undefined,
  today = new Date(),
): number | null => {
  if (!date) return null;
  const time = Date.parse(date);
  if (Number.isNaN(time)) return null;
  return Math.floor((today.getTime() - time) / DAY_MS);
};

/** "today", "yesterday", "5 d ago", then the date. */
export const formatLastTouch = (
  date: string | null | undefined,
  locale = "en",
  today = new Date(),
): string => {
  const days = daysSince(date, today);
  if (days === null || !date) return "";
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} d ago`;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(date));
};

const year = (date: string | null) => (date ? date.slice(0, 4) : "");

/** Status with what matters for it: onboarding day count, client since, etc. */
export const AccountStatus = ({ account }: { account: ZcrmAccount }) => {
  const translate = useTranslate();
  const status = ACCOUNT_STATUSES.find((s) => s.id === account.status);
  if (!status) return null;
  const label = translate(`zcrm.accounts.status.${status.id}`, {
    _: status.label,
  });
  if (status.id === "onboarding" && account.days_since_signed !== null) {
    const day = account.days_since_signed + 1;
    const late = day > ACTIVATION_TARGET_DAYS;
    return (
      <span className="flex flex-col">
        <StatusDot tone={late ? "error" : "warning"} label={label} />
        <span
          className={cn(
            "text-xs pl-3.5",
            late ? "text-destructive" : "text-muted-foreground",
          )}
        >
          Day {day} of {ACTIVATION_TARGET_DAYS}
        </span>
      </span>
    );
  }
  const since =
    account.status_source === "manual"
      ? "set by hand"
      : status.id === "active" && account.signed_on
        ? `since ${year(account.signed_on)}`
        : "";
  return (
    <span className="flex flex-col">
      <StatusDot tone={status.tone} label={label} />
      {since && (
        <span className="text-xs pl-3.5 text-muted-foreground">{since}</span>
      )}
    </span>
  );
};

export const AccountHealth = ({ value }: { value: string }) => {
  const segment = HEALTH_SEGMENTS.find((s) => s.value === value);
  if (!segment) {
    return value ? <span>{value}</span> : <EmptyValue />;
  }
  return <StatusDot tone={segment.tone} label={segment.value} />;
};

const KIND_VARIANTS: Record<string, "info" | "violet" | "neutral"> = {
  [ACCOUNT_KINDS[0]]: "info",
  [ACCOUNT_KINDS[1]]: "violet",
};

export const AccountKind = ({ value }: { value: string }) =>
  value ? (
    <Badge variant={KIND_VARIANTS[value] ?? "neutral"}>{value}</Badge>
  ) : null;

/** The next step, in red once its date has passed. */
export const NextStep = ({
  account,
  today = new Date(),
}: {
  account: Pick<ZcrmAccount, "next_step" | "next_step_due">;
  today?: Date;
}) => {
  const [locale = "en"] = useLocaleState();
  if (!account.next_step) return <EmptyValue />;
  const overdue = (daysSince(account.next_step_due, today) ?? 0) > 0;
  return (
    <span className="flex flex-col min-w-0">
      <span className="truncate">{account.next_step}</span>
      {account.next_step_due && (
        <span
          className={cn(
            "text-xs",
            overdue ? "text-destructive font-medium" : "text-muted-foreground",
          )}
        >
          {overdue ? "Overdue · " : "Due "}
          {new Intl.DateTimeFormat(locale, {
            month: "short",
            day: "numeric",
          }).format(new Date(account.next_step_due))}
        </span>
      )}
    </span>
  );
};

export const LastTouch = ({ date }: { date: string | null }) => {
  const [locale = "en"] = useLocaleState();
  const text = formatLastTouch(date, locale);
  return text ? (
    <span className="text-muted-foreground">{text}</span>
  ) : (
    <EmptyValue />
  );
};

/** Doors served out of serviceable doors ("120 / 450"), or whichever is known. */
export const formatDoors = (
  account: Pick<ZcrmAccount, "doors_served" | "serviceable_doors">,
): string => {
  const served = account.doors_served.trim();
  const total = account.serviceable_doors.trim();
  if (served && total) return `${served} / ${total}`;
  return served || total;
};

export const EmptyValue = () => (
  <span className="text-muted-foreground" aria-label="none">
    —
  </span>
);
